import {
    Action,
    API_ITEMS_GET_DONE,
    API_ITEMS_PATCH_DONE,
    SYNC_MARK_DIRTY,
    ITEM_RENAME,
    ITEM_CREATE_AFTER,
    ITEM_CREATE_BEFORE,
    ITEM_DURATION_CHANGE,
    ITEM_LOCK_CHANGE,
    ITEM_ORDER_CHANGE,
    ITEM_REMOVE,
    UI_DAY_CHANGE,
    UNDO,
    REDO,
    USER_LOG_OUT,
} from "../actions";
import { State } from ".";
import { getCurrentDay } from "./currentDay.selectors";
import { isDayPrunable } from "../model/item";

// per-date monotonic counters, bumped on every local mutation; a sync-done
// only clears a date when the pushed counter still matches, so edits made
// while a request is in flight keep their dirty mark
export type DirtyDates = { [date: string]: number };

export type SyncState = {
    // "" = never synced; also the first-login marker (upload all local days)
    lastSyncTime: string;
    dirtyDates: DirtyDates;
};

const initialState: SyncState = {
    lastSyncTime: ``,
    dirtyDates: {},
};

const markDirty = (state: SyncState, dates: Array<string>): SyncState => ({
    ...state,
    dirtyDates: dates.reduce(
        (dirtyDates, date) => {
            dirtyDates[date] = (dirtyDates[date] || 0) + 1;
            return dirtyDates;
        },
        { ...state.dirtyDates },
    ),
});

export const syncReducer = (
    state: SyncState = initialState,
    action: Action,
    entireState: State,
): SyncState => {
    switch (action.type) {
        case ITEM_RENAME:
        case ITEM_CREATE_AFTER:
        case ITEM_CREATE_BEFORE:
        case ITEM_DURATION_CHANGE:
        case ITEM_LOCK_CHANGE:
        case ITEM_ORDER_CHANGE:
        case ITEM_REMOVE:
        case UNDO:
        case REDO:
            return markDirty(state, [action.payload.date]);
        case UI_DAY_CHANGE: {
            // mirror daysReducer's pruning: entireState is the PRE-action
            // state, so ui.currentDay is still the day being navigated away
            // from; when that day is about to be dropped, its deletion must
            // reach the server as an empty-list tombstone
            const previousDay = getCurrentDay(entireState);
            if (previousDay === action.payload.date) {
                return state;
            }
            const dayData = entireState.data.days[previousDay];
            if (!isDayPrunable(dayData && dayData.present)) {
                return state;
            }
            return markDirty(state, [previousDay]);
        }
        case SYNC_MARK_DIRTY:
            return markDirty(state, action.payload.dates);
        case API_ITEMS_GET_DONE:
        case API_ITEMS_PATCH_DONE: {
            const { newSyncTime, pushedDates } = action.meta;
            const dirtyDates = Object.keys(state.dirtyDates).reduce(
                (result, date) => {
                    if (pushedDates[date] !== state.dirtyDates[date]) {
                        // not pushed, or re-dirtied mid-flight (higher counter)
                        result[date] = state.dirtyDates[date];
                    }
                    return result;
                },
                {} as DirtyDates,
            );
            return { lastSyncTime: newSyncTime, dirtyDates };
        }
        case USER_LOG_OUT:
            // local days are kept (existing web behavior); sync state is
            // per-account and must not leak into the next session
            return initialState;
        default:
            return state;
    }
};
